import { useCallback, useMemo } from 'react';
import { useGetKadryInfoQuery } from '@shared/api';
import { useAgentPermission } from '@entities/user';
import type { KadryInfo, KadryOp } from './types';

export interface KadryAccessState {
  info: KadryInfo | undefined;
  isLoading: boolean;
  error: unknown;
  /** Модуль включён и готов (`status.state === 'ready'`). */
  ready: boolean;
  /**
   * Право записи на агента `kadry` (`agent_permissions.can_write`). Без него
   * сервер отклоняет любые изменения (ADR-0019 §4, «только чтение»).
   */
  writable: boolean;
  /** Операция доступна пользователю (Прил. Б) — для показа разделов и данных. */
  can: (op: KadryOp) => boolean;
  /** Доступна хотя бы одна из операций. */
  canAny: (ops: readonly KadryOp[]) => boolean;
  /** Изменяющее действие доступно: операция есть и нет режима «только чтение». */
  canWrite: (op: KadryOp) => boolean;
  /** Пользователь назначает сотрудников на заявки (рекрутер, администратор подбора). */
  assigns: boolean;
}

/**
 * Права текущего пользователя в модуле Кадры по `/info` (`capabilities`) и
 * праву записи на агента. Сервер проверяет всё сам — UI лишь прячет
 * недоступные действия. До загрузки `/info` всё запрещено.
 */
export function useKadryAccess(): KadryAccessState {
  const { data, isLoading, error } = useGetKadryInfoQuery();
  const { can_write } = useAgentPermission('kadry');
  const caps = useMemo(() => new Set<KadryOp>(data?.capabilities ?? []), [data]);
  const can = useCallback((op: KadryOp) => caps.has(op), [caps]);
  const canAny = useCallback((ops: readonly KadryOp[]) => ops.some((op) => caps.has(op)), [caps]);
  const canWrite = useCallback((op: KadryOp) => can_write && caps.has(op), [can_write, caps]);
  const roles = data?.roles ?? [];
  return {
    info: data,
    isLoading,
    error,
    ready: data?.status?.state === 'ready',
    writable: can_write,
    can,
    canAny,
    canWrite,
    assigns: roles.includes('recruiter') || roles.includes('hr_admin'),
  };
}

/** Операции, открывающие раздел «Заявки». */
export const KADRY_REQUESTS_OPS: readonly KadryOp[] = [
  'request_create',
  'shortlist_view',
  'metrics_view',
];
